import React from 'react';
import styled, { DefaultTheme } from 'styled-components';

/** Cores de marca válidas para o estado ativo (tokens do @kardume/theme). */
export type PageButtonTone = 'primary' | 'accent' | 'petrol';

const toneColor = (t: DefaultTheme, tone: PageButtonTone): string =>
  tone === 'accent' ? t.accent : tone === 'petrol' ? t.petrol : t.primary;

const toneColorDark = (t: DefaultTheme, tone: PageButtonTone): string =>
  tone === 'accent' ? t.accentDark : tone === 'petrol' ? t.petrolDark : t.primaryDark;

/** Tint suave para hover no estado inativo - usa o primaryLight só no primary. */
const toneTint = (t: DefaultTheme, tone: PageButtonTone): string =>
  tone === 'primary' ? t.primaryLight : t.surfaceAlt;

const Btn = styled.button<{ $active?: boolean; $tone: PageButtonTone }>`
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.5rem;
  border: 1px solid ${(p) => (p.$active ? toneColorDark(p.theme, p.$tone) : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => (p.$active ? toneColor(p.theme, p.$tone) : p.theme.surface)};
  color: ${(p) => (p.$active ? p.theme.textInverted : p.theme.textSecondary)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => toneColorDark(p.theme, p.$tone)};
    background: ${(p) => (p.$active ? toneColorDark(p.theme, p.$tone) : toneTint(p.theme, p.$tone))};
    color: ${(p) => (p.$active ? p.theme.textInverted : toneColor(p.theme, p.$tone))};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${(p) => toneColor(p.theme, p.$tone)}33;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${(p) => (p.$active ? 1 : 0.45)};
  }
`;

export interface PageButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Marca o botão como a página atual (preenchido com a cor da marca). */
  active?: boolean;
  /** Cor de marca do estado ativo. Default: primary (Azul Urbano). */
  tone?: PageButtonTone;
}

/**
 * Botão quadrado reutilizável (número de página ou seta) usado pelo `Pagination`.
 * Todo o visual vem dos tokens do `@kardume/theme` - nunca hex literais.
 */
export const PageButton: React.FC<PageButtonProps> = ({
  active = false,
  tone = 'primary',
  type = 'button',
  children,
  ...rest
}) => (
  <Btn $active={active} $tone={tone} type={type} {...rest}>
    {children}
  </Btn>
);

export default PageButton;
